import { customElement, property, query, state } from "lit/decorators.js";
import { html, TemplateResult, nothing } from "lit";
import { ref } from "lit/directives/ref.js";
import { Overlay } from "@omegagrid/core";
import { msg } from "@omegagrid/localize";
import { TreeAdapter, TreeDropdown, TreeOptions, TreeSource } from "@omegagrid/tree";
import rp, { AppComponent, CustomParameter, createCustomParameterFromSqlParameter } from "@reporting/rp-app-host";
import { CsvDescription, CsvDelimiter, CsvQuote } from "@reporting/rp-utils-csv";
import { IOThreadTypes, ENCLOSURE_LIST, DECIMAL_LIST, DELIMITER_LIST, ENCODING_LIST, IOValidationMessage, IOValidationMessageType } from "../types";
import { style } from "./ioControlsContainer.style";
import { Parameter, Thread, MetadataTableColumn } from "@reporting/rp-types/etl";

const VALIDATION_ICONS: Record<IOValidationMessageType, string> = {
	hint: 'circle-info',
	info: 'circle-info',
	warning: 'triangle-exclamation',
	danger: 'circle-exclamation',
};

class IOParameterTreeAdapter extends TreeAdapter {

	private data: TreeSource;

	constructor(
		private parameter: Parameter
	) { super(); }

	async getTree(): Promise<TreeSource> {
		if (this.data) return this.data;

		try {
			this.data = await rp.ms('etl').get<TreeSource>(
				`threads/${this.parameter.id_thread || 0}/parameters/${this.parameter.idz}/content`,
				{ scope: 1 }
			);
			return this.data;
		} catch (error) {
			return [
				{ k: null, v: error, icon: { icon: "hexagon-exclamation", color: 'red' } }
			];
		}
	}

	getOptions(): Partial<TreeOptions> {
		const multiselect = /^table/.test(this.parameter.data_type);
		return {
			selectMode: multiselect ? 'multiple' : 'single',
			autoClose: !multiselect,
			allowEmpty: true
		};
	}
}

@customElement('rp-io-controlscontainer')
export class IOControlsContainer extends AppComponent {

	static styles = [style];

	@property({ type: Object })
	thread: Thread;

	@property({ type: Object })
	tableColumns: MetadataTableColumn[];

	@property({ type: Object })
	csvDescriptor: CsvDescription = {
		header: true,
		// the client default is true, which exported `NAME|type` headers the import could not match or detect the delimiter of
		headerTypes: false,
		delimiter: '\t',
		quote: '',
		decimalSeparator: ',',
		encoding: 'UTF-16LE',
	};

	@property({ type: Boolean })
	importEnabled: boolean = false;

	/** Messages explaining the state of the input data (why the import is disabled); shown under the action buttons. */
	@property({ type: Array })
	validationMessages: IOValidationMessage[] = [];

	/** Mobile layout: buttons side by side and, for an import, sections that fold away to leave room for the editor. */
	@property({ type: Boolean, reflect: true })
	compact = false;

	@state()
	private _collapsedSections = new Set<string>(['csv', 'columns']);

	@property({ type: String })
	fileName: string;

	@state()
	parameters: Parameter[] = [];

	@query('og-overlay')
	loader: Overlay;

	private _parameterComponents = new Map<string, CustomParameter>();

	get parameterComponents() { return this._parameterComponents; }

	get isImport() { return this.thread?.type === IOThreadTypes.Import; }
	get isExport() { return this.thread?.type === IOThreadTypes.Export; }
	get isDelete() { return this.thread?.type === IOThreadTypes.Delete; }

	getParameterValues(): Record<string, unknown> {
		const values: Record<string, unknown> = {};
		this._parameterComponents.forEach((param, name) => values[name] = param.value);
		return values;
	}

    updateCsvDescriptor(descriptor: Partial<CsvDescription>) {
        this.csvDescriptor = { ...this.csvDescriptor, ...descriptor };
        this._onCsvChange();
    }

	async updated(props: Map<PropertyKey, unknown>) {
		if ((props.has('thread')) && this.thread) await this.refresh();
	}

	async refresh() {
		this.loader?.show(this);

		this._parameterComponents.clear();
		const parameters = await rp.ms('etl').get<Parameter[]>(`threads/${this.thread.id_thread}/parameters`, { scope: 1 }) ?? [];
		this.parameters = parameters.filter(p => p.description);

		this.loader?.hide();
	}

	showLoader = () => this.loader?.show(this);

	hideLoader = () => this.loader?.hide();

	private _onCsvChange() {
		this.dispatchEvent(new CustomEvent('io.changeCsvDescriptor', { detail: this.csvDescriptor, bubbles: true, composed: true }));
	}

	/** Only the import has content competing for the screen; export and delete keep their sections open. */
	get collapsibleSections() { return this.compact && this.isImport; }

	private _toggleSection(id: string) {
		const collapsed = new Set(this._collapsedSections);
		if (collapsed.has(id)) collapsed.delete(id);
		else collapsed.add(id);
		this._collapsedSections = collapsed;
	}

	/** A section of the panel; in the compact import layout it folds on a header click and shows `count` so a folded section still tells what is inside. */
	private _renderSection(id: string, title: string | TemplateResult, count: number | null, body: TemplateResult, cls = ''): TemplateResult {
		const collapsible = this.collapsibleSections;
		const collapsed = collapsible && this._collapsedSections.has(id);

		return html`
			<div class="section ${cls}">
				<div class="section-header ${collapsible ? 'collapsible' : ''}" @click="${collapsible ? () => this._toggleSection(id) : null}">
					${collapsible ? html`<og-icon icon="chevron-${collapsed ? 'right' : 'down'}" fixedWidth></og-icon>` : nothing}
					${title}
					${collapsible && count != null ? html`<span class="section-count">${count}</span>` : nothing}
				</div>
				${collapsed ? nothing : body}
			</div>
		`;
	}

	private _renderParameter(p: Parameter): TemplateResult {
		let paramComponent = this._parameterComponents.get(p.name);
		if (!paramComponent) {
			paramComponent = createCustomParameterFromSqlParameter(p, null, {mode: 'user'})
			paramComponent.treeAdapter = new IOParameterTreeAdapter(p);
			this._parameterComponents.set(p.name, paramComponent);
		}

		return html`
			<div class="parameter-row">
				<div class="parameter-label" title="${p.description || p.name}">${p.description || p.name}</div>
				<div class="parameter-value">${paramComponent}</div>
			</div>
		`;
	}

	private _initializedDropdowns = new WeakSet<Element>();

	private _initDropdown(el: Element | undefined, data: TreeSource, value: string, onChange: (value: string) => void) {
		if (!el) return;
		const dd = el as TreeDropdown;
		if (!this._initializedDropdowns.has(el)) {
			this._initializedDropdowns.add(el);
			dd.setSourceData(data, { selectMode: 'single', autoClose: true });
			dd.addEventListener('change', () => {
				const sel = dd.selection?.serialize(['v']);
				const val = sel?.length > 0 ? sel[0][0] : null;
				onChange(val);
			});
		}
		if (value != null) dd.value = [[value, 0, 1]];
	}

	private _renderCsvParameters(): TemplateResult {
		if (this.isDelete) return html``;

		return this._renderSection('csv', html`CSV ${msg('Parameters')}`, null, html`
				<div class="parameter-row">
					<div class="parameter-label">${msg('Header')}</div>
					<div class="parameter-value">
						<og-form-checkbox 
						.value="${this.csvDescriptor.header}" 
					@change="${(e: any) => { this.csvDescriptor = { ...this.csvDescriptor, header: e.target.value }; this._onCsvChange(); }}">
						</og-form-checkbox>
					</div>
				</div>

				<div class="parameter-row">
					<div class="parameter-label">${msg('Delimiter')}</div>
					<div class="parameter-value">
						<og-tree-dropdown
					${ref(el => this._initDropdown(el, DELIMITER_LIST, this.csvDescriptor.delimiter, v => { this.csvDescriptor = { ...this.csvDescriptor, delimiter: v as CsvDelimiter }; this._onCsvChange(); }))}>
						</og-tree-dropdown>
					</div>
				</div>

				<div class="parameter-row">
					<div class="parameter-label">${msg('Enclosure')}</div>
					<div class="parameter-value">
						<og-tree-dropdown
					${ref(el => this._initDropdown(el, ENCLOSURE_LIST, this.csvDescriptor.quote, v => { this.csvDescriptor = { ...this.csvDescriptor, quote: (v ?? '') as CsvQuote }; this._onCsvChange(); }))}>
						</og-tree-dropdown>
					</div>
				</div>

				${this.isExport ? html`
					<div class="parameter-row">
						<div class="parameter-label">${msg('Decimal separator')}</div>
						<div class="parameter-value">
							<og-tree-dropdown
						${ref(el => this._initDropdown(el, DECIMAL_LIST, this.csvDescriptor.decimalSeparator, v => { this.csvDescriptor = { ...this.csvDescriptor, decimalSeparator: (v ?? ',') as ',' | '.' }; this._onCsvChange(); }))}>
							</og-tree-dropdown>
						</div>
					</div>
					
					<div class="parameter-row">
						<div class="parameter-label">${msg('Encoding')}</div>
						<div class="parameter-value">
							<og-tree-dropdown
						${ref(el => this._initDropdown(el, ENCODING_LIST, this.csvDescriptor.encoding ?? 'UTF-16LE', v => { this.csvDescriptor = { ...this.csvDescriptor, encoding: v ?? 'UTF-16LE' }; this._onCsvChange(); }))}>
							</og-tree-dropdown>
						</div>
					</div>
				` : nothing}
		`, 'csv-section');
	}

	private _renderInputColumns(): TemplateResult {
		return this._renderSection('columns', msg('Input columns'), this.tableColumns.length, html`
				<ul class="column-list">
					${this.tableColumns.map(col => html`
						<li class="column-item">
							<span class="column-name">${col.column}</span>
							<span class="column-type">${col.type}</span>
						</li>
					`)}
				</ul>
		`);
	}

	private _renderActions(): TemplateResult {
		let execButtonText: string;
		let execButtonIcon: string;
		let buttonColor: string;
		let disabled = false;

		if (this.isImport) {
			execButtonText = msg('Import');
			execButtonIcon = 'cloud-arrow-up';
			buttonColor = 'green';
			disabled = !this.importEnabled;
		} else if (this.isExport) {
			execButtonText = msg('Export');
			execButtonIcon = 'cloud-arrow-down';
			buttonColor = 'green';
		} else {
			execButtonText = msg('Delete');
			execButtonIcon = 'trash';
			buttonColor = 'red';
		}

		return html`
			<div class="section">
				<div class="section-header">${this.thread?.description ?? ''}</div>
				<div class="actions">
					${this.isImport ? html`
						<og-button
							color="blue"
							@click="${() => this.dispatchEvent(new CustomEvent('io.selectFile'))}">
							<div slot="content" style="display: flex; justify-content: center;" title="${this.fileName}">
								<og-icon style="margin-right: 4px;" icon="${this.fileName ? 'circle-info' : 'upload'}" color="var(--og-colortype-blue-text-color)"></og-icon>
								${this.fileName ? msg('Change file') : msg('Select file')}
							</div>
						</og-button>
					` : nothing}
					<og-button 
						color="${buttonColor}" 
						?disabled="${disabled}"
						@click="${() => this.dispatchEvent(new CustomEvent('io.execute'))}">
						<div slot="content" style="display: flex; justify-content: center;">
							<og-icon style="margin-right: 4px;" icon="${execButtonIcon}" color="var(--og-colortype-blue-text-color)"></og-icon>
							${execButtonText}
						</div>
					</og-button>
				</div>
				${this._renderValidation()}
			</div>
		`;
	}

	private _renderValidation(): TemplateResult | typeof nothing {
		if (!this.isImport || this.validationMessages.length === 0) return nothing;

		return html`
			<div class="validation">
				${this.validationMessages.map(m => m.type === 'hint'
					? html`
						<div class="validation-hint">
							<og-icon icon="${VALIDATION_ICONS.hint}"></og-icon>
							<span>${m.text}</span>
						</div>
					`
					: html`
						<og-alert type="${m.type}" icon="${VALIDATION_ICONS[m.type]}" .text="${m.text}" .noCopy="${true}"></og-alert>
					`
				)}
			</div>
		`;
	}

	layout() {}

	render() {
		return html`
			${this._renderActions()}

			${this.parameters.length > 0 ? this._renderSection(
				'parameters',
				msg('Parameters'),
				this.parameters.length,
				html`${this.parameters.map(p => this._renderParameter(p))}`
			) : nothing}

			${this._renderCsvParameters()}
			${this.tableColumns ? this._renderInputColumns() : nothing}

			<og-overlay defaultLoader></og-overlay>
		`;
	}
}
